<template>
  <div class="p-6">
    <!-- 页面标题 -->
    <div class="mb-6 flex justify-between items-center">
      <div>
        <h1 class="text-2xl font-bold text-gray-900 dark:text-gray-100">Mock 书籍分类管理</h1>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">管理 Mock 书籍的分类</p>
      </div>
      <button @click="showCreateModal = true"
        class="primary-btn">
        创建分类
      </button>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="flex justify-center items-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
    </div>

    <!-- 分类列表 -->
    <div v-else class="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900">
      <CategoryList :categories="categories" @edit="handleEdit" @delete="handleDelete" />
    </div>

    <!-- 创建分类模态框 -->
    <div v-if="showCreateModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 dark:bg-gray-900 dark:bg-opacity-70 flex items-center justify-center z-50">
      <div class="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
        <h2 class="text-xl font-bold mb-4 dark:text-gray-100">创建分类</h2>
        <form @submit.prevent="handleCreate">
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">名称（英文标识）*</label>
              <input v-model="createForm.name" type="text" required
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600"
                placeholder="例如：cambridge_ielts" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">显示名称（中文）*</label>
              <input v-model="createForm.displayName" type="text" required
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600"
                placeholder="例如：剑桥雅思" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">描述</label>
              <textarea v-model="createForm.description" rows="3"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600"
                placeholder="分类描述（可选）"></textarea>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">排序顺序</label>
              <input v-model.number="createForm.sortOrder" type="number"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600"
                placeholder="0" />
            </div>
          </div>
          <div class="mt-6 flex justify-end space-x-3">
            <button type="button" @click="showCreateModal = false"
              class="cancel-btn">
              取消
            </button>
            <button type="submit" :disabled="creating"
              class="primary-btn disabled:opacity-50">
              {{ creating ? '创建中...' : '创建' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- 编辑分类模态框 -->
    <div v-if="showEditModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 dark:bg-gray-900 dark:bg-opacity-70 flex items-center justify-center z-50">
      <div class="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
        <h2 class="text-xl font-bold mb-4 dark:text-gray-100">编辑分类</h2>
        <form @submit.prevent="handleUpdate">
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">显示名称（中文）</label>
              <input v-model="editForm.displayName" type="text"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">描述</label>
              <textarea v-model="editForm.description" rows="3"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600"></textarea>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">排序顺序</label>
              <input v-model.number="editForm.sortOrder" type="number"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600" />
            </div>
          </div>
          <div class="mt-6 flex justify-end space-x-3">
            <button type="button" @click="showEditModal = false"
              class="cancel-btn">
              取消
            </button>
            <button type="submit" :disabled="updating"
              class="primary-btn disabled:opacity-50">
              {{ updating ? '更新中...' : '更新' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- 错误提示 -->
    <div v-if="errorMessage"
      class="fixed bottom-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg shadow-lg dark:bg-red-900/30 dark:border-red-700 dark:text-red-400"
      role="alert">
      <div class="flex items-center gap-2">
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
            clip-rule="evenodd" />
        </svg>
        <span>{{ errorMessage }}</span>
        <button @click="errorMessage = ''" class="ml-2 text-red-700 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300">
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd" />
          </svg>
        </button>
      </div>
    </div>

    <!-- 成功提示 -->
    <div v-if="successMessage"
      class="fixed bottom-4 right-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg shadow-lg dark:bg-green-900/30 dark:border-green-700 dark:text-green-400"
      role="alert">
      <div class="flex items-center gap-2">
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
            clip-rule="evenodd" />
        </svg>
        <span>{{ successMessage }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import CategoryList from '@/components/mock/CategoryList.vue'
import {
  getAllCategoriesAPI,
  createCategoryAPI,
  updateCategoryAPI,
  deleteCategoryAPI,
} from '@/api/mock/category'
import type { Category, CreateCategoryRequest, UpdateCategoryRequest } from '@/types/mock/category'

// 分类列表
const categories = ref<Category[]>([])

// 加载状态
const loading = ref(false)
const creating = ref(false)
const updating = ref(false)

// 模态框状态
const showCreateModal = ref(false)
const showEditModal = ref(false)

// 表单数据
const createForm = ref<CreateCategoryRequest>({
  name: '',
  displayName: '',
  description: null,
  sortOrder: 0,
})

const editForm = ref<UpdateCategoryRequest>({
  displayName: null,
  description: null,
  sortOrder: null,
})

const editingCategory = ref<Category | null>(null)

// 消息提示
const errorMessage = ref('')
const successMessage = ref('')

/**
 * 加载所有分类
 */
const loadCategories = async () => {
  loading.value = true
  errorMessage.value = ''
  try {
    const response = await getAllCategoriesAPI()
    categories.value = response.data || []
  } catch (error) {
    console.error('加载分类失败:', error)
    errorMessage.value = '加载分类失败，请刷新页面重试'
  } finally {
    loading.value = false
  }
}

/**
 * 处理创建分类
 */
const handleCreate = async () => {
  creating.value = true
  errorMessage.value = ''
  try {
    await createCategoryAPI(createForm.value)
    successMessage.value = '创建分类成功'
    setTimeout(() => {
      successMessage.value = ''
    }, 3000)
    showCreateModal.value = false
    createForm.value = {
      name: '',
      displayName: '',
      description: null,
      sortOrder: 0,
    }
    await loadCategories()
  } catch (error: any) {
    console.error('创建分类失败:', error)
    errorMessage.value = error.response?.data?.message || '创建分类失败，请重试'
  } finally {
    creating.value = false
  }
}

/**
 * 处理编辑分类
 */
const handleEdit = (category: Category) => {
  editingCategory.value = category
  editForm.value = {
    displayName: category.displayName,
    description: category.description,
    sortOrder: category.sortOrder,
  }
  showEditModal.value = true
}

/**
 * 处理更新分类
 */
const handleUpdate = async () => {
  if (!editingCategory.value) return

  updating.value = true
  errorMessage.value = ''
  try {
    await updateCategoryAPI(editingCategory.value.id, editForm.value)
    successMessage.value = '更新分类成功'
    setTimeout(() => {
      successMessage.value = ''
    }, 3000)
    showEditModal.value = false
    editingCategory.value = null
    await loadCategories()
  } catch (error: any) {
    console.error('更新分类失败:', error)
    errorMessage.value = error.response?.data?.message || '更新分类失败，请重试'
  } finally {
    updating.value = false
  }
}

/**
 * 处理删除分类
 */
const handleDelete = async (category: Category) => {
  if (!confirm(`确定要删除分类"${category.displayName}"吗？`)) {
    return
  }

  errorMessage.value = ''
  try {
    await deleteCategoryAPI(category.id)
    successMessage.value = '删除分类成功'
    setTimeout(() => {
      successMessage.value = ''
    }, 3000)
    await loadCategories()
  } catch (error: any) {
    console.error('删除分类失败:', error)
    errorMessage.value = error.response?.data?.message || '删除分类失败，请重试'
  }
}

/**
 * 组件挂载时加载数据
 */
onMounted(() => {
  loadCategories()
})
</script>
